import { Injectable } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AssetListSharedState, AssetListStateService } from './asset-list-state.service';
import { AssetListService } from '../../../services/asset-list.service';
import { OverlayService } from '../../../services/overlayService';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AffectedAsset, Asset } from '../models/asset.model';

@Injectable({
  providedIn: 'root',
})
export class AssetSelectionService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly assetListService: AssetListService,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe
  ) {}

  public selectAsset(
    e: { target: { checked: boolean } },
    asset: Asset,
    fromCampaignDashboard: boolean,
    multiple: boolean,
    assetSelectedCallback?: (selectedAssetTypes: string[]) => void,
    getLinkedAssetsCallback?: () => void,
    ngUnsubscribe?: Subject<void>
  ): void {
    const state: AssetListSharedState = this.stateService.currentState;
    this.stateService.updateState({ isBusy: true });

    if (fromCampaignDashboard) {
      const selectedAssetTypes: string[] = [...state.selectedAssetTypes];
      const idx: number = selectedAssetTypes.indexOf(asset.id);
      if (idx >= 0) {
        selectedAssetTypes.splice(idx, 1);
      } else {
        selectedAssetTypes.push(asset.id);
      }
      this.stateService.updateState({ selectedAssetTypes, isBusy: false });

      if (assetSelectedCallback) {
        assetSelectedCallback(selectedAssetTypes);
      }
      return;
    }

    const idx: number = state.selectedAssetTypes.indexOf(asset.id);
    if (idx < 0) {
      this.assetListService
        .getAssetById(asset.id)
        .pipe(takeUntil(ngUnsubscribe))
        .subscribe({
          next: (res: Asset & { projectId: string }[]) => {
            if (res.length) {
              e.target.checked = false;
              this.overlayService.showError(
                `Asset has been selected by project "${state.projects[res[0].projectId].name}"`
              );
            } else {
              let selectedAssetTypes: string[];
              if (multiple) {
                selectedAssetTypes = [...state.selectedAssetTypes, asset.id];
              } else {
                selectedAssetTypes = [asset.id];
              }
              this.stateService.updateState({ selectedAssetTypes });

              if (assetSelectedCallback) {
                assetSelectedCallback(selectedAssetTypes);
              }
            }
            this.stateService.updateState({ isBusy: false });
          },
          error: (err: Error) => {
            this.stateService.updateState({ isBusy: false });
            this.overlayService.showError(err.message);
          },
        });
    } else {
      const affectedAssets: Record<string, AffectedAsset | null> = { ...state.affectedAssets };
      affectedAssets[asset.id] = null;
      const selectedAssetTypes: string[] = state.selectedAssetTypes.filter((id: string) => id !== asset.id);
      this.stateService.updateState({ isBusy: false, affectedAssets, selectedAssetTypes });

      if (assetSelectedCallback) {
        assetSelectedCallback(selectedAssetTypes);
      }

      if (getLinkedAssetsCallback) {
        getLinkedAssetsCallback();
      }
    }
  }

  public assetToggle(id: string): void {
    const state: AssetListSharedState = this.stateService.currentState;
    const selectedAssetTypes: string[] = [...state.selectedAssetTypes];
    const index: number = selectedAssetTypes.indexOf(id);

    if (index === -1) {
      selectedAssetTypes.push(id);
    } else {
      selectedAssetTypes.splice(index, 1);
    }

    const selectedAssetId: string[] = [...state.selectedAssetId];
    const indexInSelectedId: number = selectedAssetId.indexOf(id);

    if (indexInSelectedId === -1) {
      selectedAssetId.push(id);
    } else {
      selectedAssetId.splice(indexInSelectedId, 1);
    }

    this.stateService.updateState({ selectedAssetTypes, selectedAssetId });
  }

  public onCancel(): void {
    const state: AssetListSharedState = this.stateService.currentState;
    let selectedAssetTypes: string[] = [...state.selectedAssetTypes];

    for (const id of state.selectedAssetId) {
      if (selectedAssetTypes.includes(id)) {
        selectedAssetTypes = selectedAssetTypes.filter((assetId: string) => assetId !== id);
      } else {
        selectedAssetTypes.push(id);
      }
    }

    this.stateService.updateState({
      selectedAssetTypes,
      selectedAssetId: [],
      selectedCategory: [],
      isEditing: false,
    });
  }

  public onSave(assetSelectedCallback?: (selectedAssetTypes: Asset[]) => void): void {
    const state: AssetListSharedState = this.stateService.currentState;

    if (assetSelectedCallback) {
      assetSelectedCallback(state.selectedAssetTypes);
    }

    this.stateService.updateState({
      selectedAssetId: [],
      selectedCategory: [],
      isEditing: false,
    });

    this.overlayService.showSuccess(this.translatePipe.transform('overlayChangeSave'));
  }

  public toggleEdit(): void {
    this.stateService.updateState({ isEditing: true });
  }
}
